import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import axiosInstance, { RefreshUnavailableError, refreshAccessToken } from 'config/axiosInstance';
import {
  clearSession,
  getRole,
  getSessionIdentity,
  hasUsableAccess,
  hasUsableSession,
  storeRole,
} from 'config/session';
import { canUseSession } from 'config/sessionEnvironment';
import { LOGIN_PATH } from './pathName';

type Verification = 'pending' | 'manager' | 'denied';

/**
 * 業務ルートの認証ガード。
 *
 * ADM-01。以前はaccess tokenの有無だけを見ており、
 *  - 期限切れのtokenでも画面が開く
 *  - 役割を確認しない
 *  - そもそもrouterへ接続されていない
 * という状態だった。使えるセッションと管理者の役割の両方を確認する。
 *
 * RV-040。役割が保存されていない（または不明な値の）ときは通さない。
 * サーバーのプロフィールで役割を確かめ直し、管理者と確認できるまで
 * 業務画面を描画しない。運転者・確認失敗はセッションを捨ててログインへ戻す。
 *
 * ADM-17。access はメモリだけにあるため、再読み込み・新しいタブでは無い。
 * セッションの印があれば、HttpOnly Cookie の refresh で取り直してから描画する。
 */
const PrivateRoute = () => {
  const location = useLocation();
  const usable = hasUsableSession();
  const storedRole = getRole();

  // 管理画面を使えない接続（secure context でない・Web Locks が無い）では、Cookie を
  // 書き換える更新を送らない。ログイン画面が理由を表示する（PR #90 レビュー P2）。
  if (!usable || !canUseSession()) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  if (storedRole === 'driver') {
    // 運転者のtokenで管理画面を開かせない。
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  const verified = storedRole === 'manager' ? <Outlet /> : <RoleVerification />;
  if (!hasUsableAccess()) {
    return <SessionRestore>{verified}</SessionRestore>;
  }
  return verified;
};

type Restore = 'pending' | 'ready' | 'unavailable' | 'denied';

/** Cookie の refresh で access を取り直し、取り直せたら子を描画する。 */
const SessionRestore = ({ children }: { children: JSX.Element }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [state, setState] = useState<Restore>('pending');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const session = getSessionIdentity();
    refreshAccessToken(session)
      .then(() => {
        if (active) setState('ready');
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof RefreshUnavailableError) {
          // 通信できないだけ。セッションは消さず、再試行できるようにする。
          setState('unavailable');
          return;
        }
        // Cookie が無い・失効・別のセッションに入れ替わった。別のタブで入れ替わった
        // 新しいセッションの印は消さない。
        if (getSessionIdentity() === session) clearSession();
        setState('denied');
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  if (state === 'ready') return children;
  if (state === 'denied') {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  if (state === 'unavailable') {
    return (
      <div role="alert" className="p-4 space-y-2">
        <p>{t('ui.error-network')}</p>
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 rounded"
          onClick={() => {
            setState('pending');
            setAttempt((n) => n + 1);
          }}
        >
          {t('ui.retry')}
        </button>
      </div>
    );
  }
  return (
    <div role="status" aria-busy="true" className="p-4">
      {t('ui.loading')}
    </div>
  );
};

/** 保存された役割が無いセッションを、サーバーの役割で確かめ直す。 */
const RoleVerification = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [state, setState] = useState<Verification>('pending');

  useEffect(() => {
    let active = true;
    // 確認を始めたときのセッション。応答待ちの間に別のタブでセッションが入れ替わったら、
    // 結果をそのセッションに当てはめない（新しいセッションのトークンを消さない・
    // 役割を書き込まない）。このタブだけログイン画面へ戻す。
    const session = getSessionIdentity();
    const replaced = () => getSessionIdentity() !== session;
    axiosInstance
      .get('/v1/profile/')
      .then((response) => {
        if (!active) return;
        if (replaced()) {
          setState('denied');
          return;
        }
        if (response?.data?.role === 'manager') {
          storeRole('manager');
          setState('manager');
        } else {
          clearSession();
          setState('denied');
        }
      })
      .catch(() => {
        if (!active) return;
        // 役割を確かめられないセッションは通さない（fail closed）。
        if (!replaced()) clearSession();
        setState('denied');
      });
    return () => {
      active = false;
    };
  }, []);

  if (state === 'manager') return <Outlet />;
  if (state === 'denied') {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  return (
    <div role="status" aria-busy="true" className="p-4">
      {t('ui.loading')}
    </div>
  );
};

export default PrivateRoute;
